import {Filter} from '@github-ui/filter'
import {hpcAttrs} from '@github-ui/hpc'
import {useQuery} from '@github-ui/react-query'
import {useParams} from '@github-ui/react-router'
import {useRepoIssuesSidebarLayoutEnabled} from '@github-ui/repo-issues-page-layout/useSidebarLayoutEnabled'
import {useNavigate, useSearchParams} from '@github-ui/use-navigate'
import {Heading} from '@primer/react'
import {clsx} from 'clsx'
import {type ReactNode, useCallback, useEffect, useRef, useState} from 'react'

import {LABELS} from '../../constants'
import {useSavedViewEndpoints} from '../../contexts/SavedViewEndpointsContext'
import {useCreateSavedViewMutation} from '../../hooks/use-create-saved-view-mutation'
import {useSavedViewAnalytics} from '../../hooks/use-saved-view-analytics'
import type {UseSavedViewEditorReturn} from '../../hooks/use-saved-view-editor'
import {useSavedViewFilterProviders} from '../../hooks/use-saved-view-filter-providers'
import {useSavedViewFilterSettings} from '../../hooks/use-saved-view-filter-settings'
import {savedViewsQueryOptions} from '../../queries'
import {SAVED_VIEW_OPEN_SOURCES, type SavedViewOpenLocationState} from '../../saved-view-open-source'
import type {
  SavedViewIssuesRenderer,
  SavedViewPlacementOwnerType,
  SavedViewsResponse,
  SavedViewVisibility,
} from '../../types'
import {viewVisibilityFromParam} from '../../utils/views-visibility'
import {SavedViewMutationErrorBanner} from '../dialogs/SavedViewMutationErrorBanner'
import {ShortcutEditingHeader} from '../ShortcutEditingHeader'
import styles from './RepositoryViewNewPage.module.css'
import {RepositoryViewNewPreview} from './RepositoryViewNewPreview'
import {VisibilitySelect} from './VisibilitySelect'

interface RepositoryViewNewContentProps<TConfiguration> {
  // Default to the repository in the route. Account level surfaces are not under
  // a repository, so they pass their own pair.
  owner?: string
  repo?: string
  editor: UseSavedViewEditorReturn
  /** Hosts that hold `configuration` include it in their dirty check. */
  confirmDiscardIfDirty: () => Promise<boolean>
  IssuesRenderer?: SavedViewIssuesRenderer
  sidebarLayoutEnabled?: boolean
  initialData?: SavedViewsResponse
  placementOwnerType?: SavedViewPlacementOwnerType
  configurationFields?: ReactNode
  /** Sent with the new view as-is. */
  configuration?: TConfiguration
  /**
   * Replaces the `IssuesRenderer` preview. It is handed that list preview, or null without an
   * `IssuesRenderer`, so it can keep it on screen while it loads.
   */
  preview?: (list: ReactNode) => ReactNode
}

export function RepositoryViewNewContent<TConfiguration = never>({
  owner: ownerProp,
  repo: repoProp,
  editor,
  confirmDiscardIfDirty,
  IssuesRenderer,
  sidebarLayoutEnabled: sidebarLayoutEnabledProp,
  initialData,
  placementOwnerType,
  configurationFields,
  configuration,
  preview,
}: RepositoryViewNewContentProps<TConfiguration>) {
  const {owner: routeOwner = '', repo: routeRepo = ''} = useParams<{owner: string; repo: string}>()
  const owner = ownerProp ?? routeOwner
  const repo = repoProp ?? routeRepo
  const navigate = useNavigate()
  const repositorySidebarLayoutEnabled = useRepoIssuesSidebarLayoutEnabled()
  const sidebarLayoutEnabled = sidebarLayoutEnabledProp ?? repositorySidebarLayoutEnabled

  const {basePath: viewsPath, listUrl} = useSavedViewEndpoints(owner, repo)

  const {data: savedViewsData} = useQuery(savedViewsQueryOptions({owner, repo, listUrl, initialData}))
  const viewerCanManageViews = savedViewsData?.viewerCanManageViews
  useEffect(() => {
    if (viewerCanManageViews === false) {
      navigate(viewsPath, {replace: true})
    }
  }, [navigate, viewerCanManageViews, viewsPath])

  const {trackNewVisited} = useSavedViewAnalytics()
  const {shortcut, setShortcutValue, previewQuery, commitPreviewQuery, previewQueryContextValue} = editor

  const filterProviders = useSavedViewFilterProviders({owner, repo})
  const filterSettings = useSavedViewFilterSettings()

  const {mutate, isPending, error} = useCreateSavedViewMutation<TConfiguration>({owner, repo})

  const [searchParams] = useSearchParams()
  const visibilityParam = searchParams.get('visibility')
  const requestedVisibility = viewVisibilityFromParam(visibilityParam)
  const [visibility, setVisibility] = useState<SavedViewVisibility>(() => requestedVisibility ?? 'personal')
  const hasTrackedVisitRef = useRef(false)
  useEffect(() => {
    if (hasTrackedVisitRef.current) return
    hasTrackedVisitRef.current = true
    const viewMetadata = placementOwnerType
      ? {
          placementOwnerType,
          searchType: 'issues' as const,
          visibilityIsDefault: requestedVisibility === undefined,
        }
      : undefined
    trackNewVisited(visibility, viewMetadata)
  }, [placementOwnerType, requestedVisibility, trackNewVisited, visibility])

  const handleDismiss = useCallback(async () => {
    if (await confirmDiscardIfDirty()) {
      navigate(viewsPath)
    }
  }, [confirmDiscardIfDirty, navigate, viewsPath])

  const handleSave = useCallback(() => {
    mutate(
      {
        name: shortcut.name.trim(),
        query: shortcut.query.trim(),
        description: shortcut.description?.trim() || undefined,
        icon: shortcut.icon,
        color: shortcut.color,
        source: 'new',
        visibility,
        ...(configuration !== undefined ? {configuration} : {}),
      },
      {
        onSuccess: ({savedView}) => {
          const openState: SavedViewOpenLocationState = {savedViewOpenSource: SAVED_VIEW_OPEN_SOURCES.created}
          navigate(`${viewsPath}/${encodeURIComponent(savedView.id)}`, {state: openState})
        },
      },
    )
  }, [configuration, mutate, navigate, shortcut, viewsPath, visibility])

  if (viewerCanManageViews === false) {
    return null
  }

  return (
    <div className={clsx(!sidebarLayoutEnabled && styles.middlePaneWrapper)} {...hpcAttrs()}>
      {!sidebarLayoutEnabled && (
        <Heading as="h1" variant="medium">
          {LABELS.views.createLink}
        </Heading>
      )}
      <div className={styles.middlePaneGrid}>
        <div className={styles.form}>
          <SavedViewMutationErrorBanner
            message={error ? LABELS.views.createError : null}
            onRetry={handleSave}
            isRetrying={isPending}
          />
          <ShortcutEditingHeader
            shortcut={shortcut}
            setShortcutValue={setShortcutValue}
            onSave={handleSave}
            onDismiss={handleDismiss}
            isSaving={isPending}
            trailingField={<VisibilitySelect value={visibility} onChange={setVisibility} />}
            leadingActions={configurationFields}
            renderFilter={({inputRef, className}) => (
              <Filter
                id="repo-view-new-filter"
                className={className}
                inputRef={inputRef}
                label={LABELS.views.filterQueryLabel}
                placeholder={LABELS.views.filterQueryPlaceholder}
                variant="input"
                providers={filterProviders}
                filterValue={shortcut.query}
                onChange={value => setShortcutValue('query', value)}
                onSubmit={request => commitPreviewQuery(request.raw)}
                settings={filterSettings}
              />
            )}
          />
        </div>
        {IssuesRenderer || preview ? (
          <RepositoryViewNewPreview
            owner={owner}
            repo={repo}
            query={previewQuery}
            previewQueryContextValue={previewQueryContextValue}
            IssuesRenderer={IssuesRenderer}
            preview={preview}
          />
        ) : null}
      </div>
    </div>
  )
}
